import { useEffect, useSyncExternalStore } from "react";
import { queryClient } from "../queryClient";
import { queryKeys, useShopStatusQuery } from "../queries";
import { isPreviewMode, setPreviewMode, subscribePreview, type ShopStatus } from "../services/shopService";

/**
 * The current shop status, shared by the header, shop page and product page
 * (one request per mode). `null` while the first fetch is in flight, then a
 * `ShopStatus`. A deep link from the admin panel (`?preview=1`) turns preview
 * mode on for the session, so the user can move around the shop without the
 * flag in every URL. If the API can't be reached it is "off": better to hide a
 * disabled shop than to flash an open one we can't actually serve.
 */
export function useShopStatus(): ShopStatus | null {
  const preview = useSyncExternalStore(subscribePreview, isPreviewMode);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("preview") === "1" && !isPreviewMode()) setPreviewMode(true);
  }, []);
  const { data, isError } = useShopStatusQuery(preview);
  return data ?? (isError ? FALLBACK_STATUS : null);
}

const FALLBACK_STATUS: ShopStatus = {
  enabled: false,
  preview: false,
  is_admin: false,
  paypal_handle: "",
  venmo_handle: "",
};

/** Force a re-fetch (used by the admin settings panel after a toggle). */
export function refreshShopStatus(): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.shopStatuses });
}

/** Imperative kill-switch for the preview banner / admin "exit preview" link. */
export function exitShopPreview(): void {
  setPreviewMode(false);
}
